import React, { PureComponent, ReactNode } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import Fuse from 'fuse.js';
import groupBy from 'lodash/groupBy';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';

import { FilesEntity, SmartReport, VerifiedEntity } from '../../api/fetchAllMedicalRecordsInADoc';
import { Interpretation, LabVitalEntity } from '../../store/types/prescription';
import { AntTooltip } from './AntTooltip';
import { MISC_KEYS, getItemFromMisc } from '../../store/idbHandler';
import { LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION } from '../../labTestsConstants';
import { GlobalState } from '../../store/reducers';
import { Actions } from '../../store/actions';
import { starKeyVital } from '../../store/actions/session';
import { track } from '../../tracking';
import { notification } from 'antd';
import { downloadIcon, infoIcon } from './Icons';
import { getMyDbId } from '../../api/mactiveid';
import { MR_PDF } from '../../routes/paths';

const fuseConfig = {
    keys: ['name'],
    threshold: 0.2,
    ignoreLocation: true,
};

export const SOURCE_MEDICAL_RECORDS = 'MEDICAL_RECORDS';
export const NO_INTERPRETATION_VITAL = 'sm-5612225938';

type MVP = {
    files: FilesEntity[];
    smartReport?: SmartReport;
    selectedIndex?: number;
    closeMe?: () => void;
    copyToPrescription?: (items: LabVitalEntity[]) => void;
    smartReportHandler?: () => void;
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    mediaViewerClassName?: string;
};

type MVS = {
    visibleFileIndex?: number;
    recordUrlLoading?: 'loading' | 'error' | 'success' | '';
    selectedSRVitals: Set<number>;

    vitalSearch: string;
    keyVitals?: string[];
    sortKeyVitals?: boolean;
    verifiedFilterList?: VerifiedEntity[] | null | undefined;
    filter_id?: MRFilters;
    smartVitalFilters: {
        [key: string]: { filter_id: string; length: number; callback: () => void };
    };
    unverifiedList: (VerifiedEntity & { unverified?: boolean })[] | null | undefined;
    tabType: TAB_TYPE;
};

type TAB_TYPE = 'smart-report' | 'details';

let deg = 0;

export const getColorClass = (
    value?: string,
    defaultValue: string = 'text-darwin-neutral-600',
    normalValue: string = 'text-darwin-neutral-600',
): string => {
    switch (value) {
        case 'sm-2631771970':
        case 'sm-1220479757':
        case 'sm-4067860500':
        case 'sm-5279274814':
        case 'sm-2631712380':
        case 'sm-1420480405':
        case 'sm-4067205096':
        case 'sm-5279215230':
            return 'text-red-primary font-700 fill-red-primary';

        case 'sm-8146614980':
            return normalValue;

        default:
            return defaultValue;
    }
};

function getOutOfRangeVitals(groupedVitalList?: { [k: string]: VerifiedEntity[] }) {
    let outOfRangeVitals: VerifiedEntity[] = [];

    if (!groupedVitalList) {
        return {
            outOfRangeVitals,
            length: 0,
        };
    }

    Object.keys(LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION).forEach((v) => {
        if (groupedVitalList[v]) {
            outOfRangeVitals = [...outOfRangeVitals, ...groupedVitalList[v]];
        }
    });

    return {
        outOfRangeVitals,
        length: outOfRangeVitals.length,
    };
}

export const sortKeyvitals = (
    filteredSmartVitals: VerifiedEntity[] | undefined,
    keyVitals: string[],
) => {
    const keyvitals = filteredSmartVitals?.filter((v) => keyVitals!.includes(v.eka_id!));

    const nonKeyVitals = filteredSmartVitals?.filter((v) => !keyVitals!.includes(v.eka_id!));

    return [...(keyvitals ?? []), ...(nonKeyVitals ?? [])];
};

type MRFilters = 'all_filters' | 'unrecognized' | 'out_of_range' | 'no_interpretation';

class MediaViewer extends PureComponent<MVP, MVS> {
    state: MVS = {
        visibleFileIndex:
            this.props.selectedIndex !== undefined
                ? this.props.selectedIndex
                : this.props.files && this.props.files.length > 0
                ? 0
                : undefined,
        recordUrlLoading: this.props.files && this.props.files.length > 0 ? 'loading' : undefined,
        selectedSRVitals: new Set<number>(),

        vitalSearch: '',
        keyVitals: undefined,
        sortKeyVitals: true,
        verifiedFilterList:
            this.props.smartReport?.verified && this.props.smartReport?.verified.length !== 0
                ? this.props.smartReport?.verified.filter((v) => v.eka_id)
                : undefined,
        filter_id: 'all_filters',
        smartVitalFilters: {},
        unverifiedList:
            [
                ...(this.props.smartReport?.unverified ?? []),
                ...(this.props.smartReport?.verified ?? [])?.filter((v) => !v.eka_id),
            ].length !== 0
                ? [
                      ...(this.props.smartReport?.unverified ?? []),
                      ...(this.props.smartReport?.verified ?? [])?.filter((v) => !v.eka_id),
                  ].map((v: VerifiedEntity) => {
                      return { ...v, unverified: true };
                  })
                : [],
        tabType: 'smart-report',
    };

    fuse = new Fuse(this.state.verifiedFilterList || [], fuseConfig);

    componentDidMount(): void {
        getItemFromMisc<string[]>(getMyDbId() || '', MISC_KEYS.keyVitals)
            .then((keyVitals) => {
                this.setState({ keyVitals });
            })
            .catch((e) => console.error(e));

        const verifiedVitals = this.state.verifiedFilterList;
        const unVerifiedVitals = this.state.unverifiedList;
        const totalVitals =
            verifiedVitals || unVerifiedVitals
                ? [...(verifiedVitals ?? []), ...(unVerifiedVitals ?? [])]
                : undefined;

        const groupedVitalList = totalVitals ? groupBy(totalVitals, 'result_id') : undefined;

        const outOfRangeVitals = getOutOfRangeVitals(groupedVitalList);
        const noInterpretationVitals = groupedVitalList
            ? groupedVitalList[NO_INTERPRETATION_VITAL] ?? []
            : [];

        this.smartVitalFiltersCallback(totalVitals, 'all_filters', totalVitals);

        const smartVitalFilters: {
            [key: string]: { filter_id: string; length: number; callback: () => void };
        } = {
            'All vitals': {
                filter_id: 'all_filters',
                length: totalVitals ? totalVitals.length : 0,
                callback: () =>
                    this.smartVitalFiltersCallback(totalVitals, 'all_filters', totalVitals),
            },
            Unrecognized: {
                filter_id: 'unrecognized',
                length: unVerifiedVitals ? unVerifiedVitals.length : 0,
                callback: () =>
                    this.smartVitalFiltersUnverifiedCallback(
                        unVerifiedVitals,
                        'unrecognized',
                        unVerifiedVitals,
                    ),
            },
            'Out of range': {
                filter_id: 'out_of_range',
                length: outOfRangeVitals.length,
                callback: () =>
                    this.smartVitalFiltersCallback(
                        outOfRangeVitals.outOfRangeVitals,
                        'out_of_range',
                        outOfRangeVitals.outOfRangeVitals,
                    ),
            },
            'No interpretation': {
                filter_id: 'no_interpretation',
                length: noInterpretationVitals.length,
                callback: () =>
                    this.smartVitalFiltersCallback(
                        noInterpretationVitals,
                        'no_interpretation',
                        noInterpretationVitals,
                    ),
            },
        };

        this.setState({ smartVitalFilters });
    }

    onToggleVitalCheckbox = (id: number): void => {
        const { selectedSRVitals } = this.state;
        if (selectedSRVitals.has(id)) {
            selectedSRVitals.delete(id);
        } else {
            selectedSRVitals.add(id);
        }
        this.setState({ selectedSRVitals: new Set(selectedSRVitals) });
    };

    onVitalSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({ vitalSearch: e.target.value });
    };

    onToggleAll = (
        vitals?:
            | (VerifiedEntity & {
                  unverified?: boolean | undefined;
              })[]
            | undefined,
        onSelect?: () => void,
    ): void => {
        const { selectedSRVitals } = this.state;

        track('select_all_smart_vitals', { nos: selectedSRVitals.size.toString() });

        if (!vitals) {
            this.setState({ selectedSRVitals: new Set() });
        } else {
            const ids = vitals ? vitals.filter((t) => t.eka_id).map((vital) => vital.id) : [];
            this.setState({ selectedSRVitals: new Set(ids) }, () => {
                onSelect && onSelect();
            });
        }
    };

    copyToPrescription = (): void => {
        const { copyToPrescription } = this.props;

        if (!copyToPrescription) {
            return;
        }

        const { selectedSRVitals } = this.state;

        if (selectedSRVitals.size === 0) {
            notification.info({ message: 'Select any vital to copy to Prescription', duration: 5 });
            return;
        }

        const t = Array.from(selectedSRVitals).map((vitalId) => {
            const option = this.props.smartReport?.verified?.find(
                (v) => v.id.toString() === vitalId.toString(),
            );

            if (!option || !option.eka_id) {
                console.log("Can't find vital with id: ", vitalId, option);
                return;
            }

            const intterpretation: Interpretation | undefined = {
                id: option.result_id,
                value: option.display_result,
            };

            return {
                id: option.eka_id,
                name: option.name,
                unit_dislay_name: option.name,
                value: option.value,
                interpretation: intterpretation,
                // date should be aaded up the parent
                remark: undefined,

                track: {
                    source: SOURCE_MEDICAL_RECORDS,
                },

                unit: { name: option.unit, id: option.unit_eka_id },
            };
        });

        this.setState({ selectedSRVitals: new Set() });
        copyToPrescription(t.filter(Boolean) as LabVitalEntity[]);
    };

    handleRotate = (): void => {
        const img = document.getElementById('rotate-img');

        if (!img) {
            return;
        }

        img.classList.remove(`rotate-${deg}`);
        deg = (deg + 90) % 360;

        img.classList.add(`rotate-${deg}`);

        if (deg === 0) {
            img.style.left = `${0}px`;
            img.style.top = `${0}px`;
        } else if (deg === 90) {
            img.style.left = `${img.clientHeight}px`;
        } else if (deg === 180) {
            img.style.top = `${img.clientHeight}px`;
            img.style.left = `${img.clientWidth}px`;
        } else if (deg === 270) {
            img.style.left = `${0}px`;
            img.style.top = `${img.clientWidth}px`;
        }
    };

    onNext = (): void => {
        const { visibleFileIndex } = this.state;
        const { files } = this.props;

        if (files && visibleFileIndex !== undefined && files?.length - 1 > visibleFileIndex) {
            this.setState({
                visibleFileIndex: visibleFileIndex + 1,
            });
        }
    };

    isNextDisabled = (): boolean => {
        const { visibleFileIndex } = this.state;
        const { files } = this.props;

        if (files && visibleFileIndex !== undefined && files?.length - 1 > visibleFileIndex) {
            return false;
        }
        return true;
    };

    onPrevious = (): void => {
        const { visibleFileIndex } = this.state;

        if (visibleFileIndex !== undefined && visibleFileIndex > 0) {
            this.setState({
                visibleFileIndex: visibleFileIndex - 1,
            });
        }
    };

    isPreviousDisabled = (): boolean => {
        const { visibleFileIndex } = this.state;

        if (visibleFileIndex !== undefined && visibleFileIndex > 0) {
            return false;
        }
        return true;
    };

    toggleKeyVital = async (vitalId: string): Promise<void> => {
        const currKeyVitals = this.state.keyVitals ?? [];

        let newKeyVitals = [];

        const doesExist = currKeyVitals.includes(vitalId);

        if (doesExist) {
            newKeyVitals = currKeyVitals.filter((v) => v !== vitalId);
        } else {
            newKeyVitals = [...currKeyVitals, vitalId];
        }

        this.setState({
            keyVitals: newKeyVitals,
            sortKeyVitals: true,
        });

        track('UPDATE_STAR_VITALS', { from: 'medical-records-smart-report' });

        this.props.dispatch(starKeyVital(newKeyVitals));
    };

    smartVitalFiltersCallback = (
        verifiedFilterList: VerifiedEntity[] | undefined,
        filter_id: MRFilters,
        fuseInput: VerifiedEntity[] | undefined,
    ): void => {
        this.fuse = new Fuse(fuseInput || [], fuseConfig);
        this.setState({ verifiedFilterList, filter_id, unverifiedList: undefined });
    };

    smartVitalFiltersUnverifiedCallback = (
        unVerifiedFilterList: (VerifiedEntity & { unverified?: boolean })[] | null | undefined,
        filter_id: MRFilters,
        fuseInput: (VerifiedEntity & { unverified?: boolean })[] | null | undefined,
    ): void => {
        this.fuse = new Fuse(fuseInput || [], fuseConfig);
        this.setState({
            unverifiedList: unVerifiedFilterList,
            filter_id,
            verifiedFilterList: undefined,
        });
    };

    copyAll = (finalVitalList: VerifiedEntity[] | undefined) => {
        if (finalVitalList && finalVitalList.length !== 0) {
            this.onToggleAll(finalVitalList, this.copyToPrescription);
        } else {
            this.onToggleAll(finalVitalList);
        }
    };

    setTabType = (type: TAB_TYPE) => {
        this.setState({ tabType: type });
    };

    render(): ReactNode {
        const { files, closeMe, smartReport, mediaViewerClassName } = this.props;

        const {
            visibleFileIndex,
            recordUrlLoading,
            selectedSRVitals,
            vitalSearch,
            sortKeyVitals,
            verifiedFilterList,
            smartVitalFilters,
            filter_id,
            keyVitals,
            tabType,
        } = this.state;

        const filterKeys =
            smartVitalFilters['Unrecognized'] && smartVitalFilters['Unrecognized'].length !== 0
                ? Object.keys(smartVitalFilters)
                : Object.keys(smartVitalFilters).filter((v) => v !== 'Unrecognized');

        const visibleFileUrl =
            files && visibleFileIndex !== undefined ? files[visibleFileIndex].asset_url : undefined;
        const visibleFileType =
            files && visibleFileIndex !== undefined ? files[visibleFileIndex].file_type : undefined;

        const filteredSmartVitals: VerifiedEntity[] | undefined = vitalSearch
            ? this.fuse.search(vitalSearch).map((t) => t.item)
            : verifiedFilterList || undefined;

        const finalVitalList: (VerifiedEntity & { unverified?: boolean })[] | undefined =
            filteredSmartVitals && this.state.keyVitals && sortKeyVitals
                ? sortKeyvitals(filteredSmartVitals, this.state.keyVitals)
                : filteredSmartVitals;

        const totalVitals =
            vitalSearch && filter_id === 'unrecognized'
                ? finalVitalList
                : [...(finalVitalList ?? []), ...(this.state.unverifiedList ?? [])];

        const fSmartReport: {
            all: (VerifiedEntity & { unverified?: boolean })[] | undefined;
        } = {
            all: totalVitals,
        };

        const totalReports =
            // @ts-expect-error ts version upgrade
            [...(smartReport?.verified ?? []), ...(smartReport?.unverified ?? [])] || [];

        const keyVitalsForCurrentReport =
            keyVitals && finalVitalList && finalVitalList.length !== 0
                ? finalVitalList
                      .filter((v) => keyVitals!.includes(v.eka_id as any))
                      .map((kv) => kv.eka_id)
                : undefined;

        const finalListVerified = finalVitalList?.filter((v) => !v.unverified);

        const mrPdfUrl = `${MR_PDF}?urlValue=${encodeURIComponent(
            visibleFileUrl || '',
        )}&fileType=${visibleFileType}`;

        return (
            <div className="flex w-full h-full overflow-hidden relative bg-white">
                <span className="rotate rotate-0 rotate-90 rotate-180 rotate-270 hidden h-0 w-0"></span>

                <div className={`${mediaViewerClassName ?? 'flex flex-col h-full w-full'}`}>
                    <div
                        className={`w-full h-full overflow-auto relative ${
                            recordUrlLoading !== 'success' ? 'flex flex-1' : ''
                        }`}
                    >
                        <div className="z-10 flex items-center space-x-16 absolute right-32 top-4 cursor-pointer select-none text-13">
                            {recordUrlLoading === 'success' && visibleFileType === 'IMG' && (
                                <div
                                    onClick={this.handleRotate}
                                    className="w-40 h-40 bg-darwin-neutral-1000 rounded-4 flex items-center justify-center cursor-pointer"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className="fill-white w-16 h-16"
                                    >
                                        <path d="M496 72v128C496 213.3 485.3 224 472 224h-128c-13.19 0-24-10.66-24-23.98c0-6.247 2.432-12.39 7.033-16.99L379.2 130.9C346.9 99.29 303.1 80 256 80C158.1 80 80 158.1 80 256s78.97 176 176 176c81.21 0 107.1-46.34 127.1-46.34c10.18 0 24.02 8.003 24.02 24.01c0 25.11-75.1 70.27-152.1 70.27c-123.5 0-223.9-100.4-223.9-223.9s100.4-223.1 223.9-223.1c60.03 0 116 24.58 157.1 64.95l41.1-41.1c4.601-4.601 10.74-7.019 16.99-7.019C485.4 48.01 496 58.83 496 72z"></path>
                                    </svg>
                                </div>
                            )}
                            <div
                                onClick={() => window.open(visibleFileUrl)}
                                className="flex items-center space-x-8 px-16 py-10 rounded-4 bg-darwin-neutral-1000"
                            >
                                <div className="flex items-center justify-center w-16 h-16">
                                    {downloadIcon('fill-white w-16 h-16')}
                                </div>
                            </div>
                            <div
                                onClick={() => window.open(mrPdfUrl)}
                                className="flex items-center space-x-8 px-16 py-10 rounded-4 bg-darwin-neutral-1000"
                            >
                                <div className="flex items-center justify-center w-16 h-16">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 448 512"
                                        className="fill-white w-16 h-16"
                                    >
                                        <path d="M136 32h-112C10.75 32 0 42.75 0 56v112C0 181.3 10.75 192 24 192C37.26 192 48 181.3 48 168V80h88C149.3 80 160 69.25 160 56S149.3 32 136 32zM424 32h-112C298.7 32 288 42.75 288 56c0 13.26 10.75 24 24 24h88v88C400 181.3 410.7 192 424 192S448 181.3 448 168v-112C448 42.75 437.3 32 424 32zM136 432H48v-88C48 330.7 37.25 320 24 320S0 330.7 0 344v112C0 469.3 10.75 480 24 480h112C149.3 480 160 469.3 160 456C160 442.7 149.3 432 136 432zM424 320c-13.26 0-24 10.75-24 24v88h-88c-13.26 0-24 10.75-24 24S298.7 480 312 480h112c13.25 0 24-10.75 24-24v-112C448 330.7 437.3 320 424 320z"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>
                        {recordUrlLoading === 'loading' && visibleFileType === 'IMG' && (
                            <div className="flex flex-1 justify-center items-center">
                                <div className="pulse">
                                    <svg width="16px" height="12px">
                                        <polyline
                                            id="back"
                                            points="1 6 4 6 6 11 10 1 12 6 15 6"
                                        ></polyline>
                                        <polyline
                                            id="front"
                                            points="1 6 4 6 6 11 10 1 12 6 15 6"
                                        ></polyline>
                                    </svg>
                                </div>
                            </div>
                        )}
                        {recordUrlLoading === 'error' && visibleFileType === 'IMG' && (
                            <div className="flex flex-1 flex-col justify-center items-center select-none">
                                <div className="w-64 h-64">
                                    <svg
                                        width="64"
                                        height="64"
                                        viewBox="0 0 384 512"
                                        className="fill-neutral-3"
                                    >
                                        <path d="M369.9,97.9,286,14A48,48,0,0,0,252.1-.1H48A48.16,48.16,0,0,0,0,48V464a48,48,0,0,0,48,48H336a48,48,0,0,0,48-48V131.9A48.23,48.23,0,0,0,369.9,97.9Zm-22.6,22.7a15.73,15.73,0,0,1,4.2,7.4H256V32.5a15.73,15.73,0,0,1,7.4,4.2ZM336,480H48a16,16,0,0,1-16-16V48A16,16,0,0,1,48,32H224V136a23.94,23.94,0,0,0,24,24H352V464A16,16,0,0,1,336,480ZM262.71,369.05l-5.66,5.66a12,12,0,0,1-17,0L192,326.63l-48.08,48.08a12,12,0,0,1-17,0l-5.66-5.66a12,12,0,0,1,0-17L169.37,304l-48.08-48.08a12,12,0,0,1,0-17l5.66-5.66a12,12,0,0,1,17,0L192,281.37l48.08-48.08a12,12,0,0,1,17,0l5.66,5.66a12,12,0,0,1,0,17L214.63,304l48.08,48.08A12,12,0,0,1,262.71,369.05Z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-neutral-3">Failed to load medical record</p>
                                </div>
                            </div>
                        )}

                        {visibleFileUrl &&
                            (visibleFileType === 'IMG' ? (
                                <img
                                    key={visibleFileUrl}
                                    id="rotate-img"
                                    style={
                                        recordUrlLoading === 'success'
                                            ? { transformOrigin: 'top left' }
                                            : {
                                                  display: 'none',
                                                  transformOrigin: 'top left',
                                              }
                                    }
                                    className="transform-gpu relative block ease-in-out duration-200 rotate-0 w-full"
                                    src={visibleFileUrl}
                                    onLoad={() => {
                                        this.setState({
                                            recordUrlLoading: 'success',
                                        });
                                    }}
                                    onError={() => {
                                        this.setState({
                                            recordUrlLoading: 'error',
                                        });
                                    }}
                                ></img>
                            ) : visibleFileType === 'PDF' ? (
                                <iframe
                                    className="h-full w-full"
                                    src={`/pdfjs-3/web/viewer.html?file=${encodeURIComponent(
                                        visibleFileUrl,
                                    )}`}
                                />
                            ) : visibleFileType === 'VID' ? (
                                <video src={visibleFileUrl} controls />
                            ) : visibleFileType === 'AUD' ? (
                                <div className="h-full flex items-center w-full">
                                    <AudioPlayer
                                        autoPlay
                                        src={visibleFileUrl}
                                        onPlay={(e) => console.log('onPlay')}
                                        // other props here
                                    />
                                </div>
                            ) : null)}
                    </div>
                    <div className="flex justify-between items-center p-16 bg-white w-full">
                        <div>
                            <button
                                onClick={this.onPrevious}
                                className="btn justify-center secondary-btn px-12 py-8 btn--small"
                                disabled={this.isPreviousDisabled()}
                            >
                                <span className="pr-8 ">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 256 512"
                                        className="cursor-pointer fill-actionPrimary"
                                    >
                                        <path d="M231.293 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L70.393 256 251.092 74.87c4.686-4.686 4.686-12.284 0-16.971L231.293 38.1c-4.686-4.686-12.284-4.686-16.971 0L4.908 247.515c-4.686 4.686-4.686 12.284 0 16.971L214.322 473.9c4.687 4.686 12.285 4.686 16.971-.001z" />
                                    </svg>
                                </span>
                                <span className=" text-center">Previous</span>
                            </button>
                        </div>
                        <div>
                            <p className="font-700 text-neutral-5">
                                <span className="text-actionPrimary">
                                    {(visibleFileIndex || 0) + 1}
                                </span>
                                /{files?.length}
                            </p>
                        </div>
                        <div>
                            <button
                                onClick={this.onNext}
                                className="secondary-btn btn px-12 py-8 btn--small justify-center"
                                disabled={this.isNextDisabled()}
                            >
                                <span className=" text-center">Next</span>
                                <span className="pl-8">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 256 512"
                                        className="fill-actionPrimary cursor-pointer fill-white"
                                    >
                                        <path d="M24.707 38.101L4.908 57.899c-4.686 4.686-4.686 12.284 0 16.971L185.607 256 4.908 437.13c-4.686 4.686-4.686 12.284 0 16.971L24.707 473.9c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L41.678 38.101c-4.687-4.687-12.285-4.687-16.971 0z" />
                                    </svg>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
                {smartReport && fSmartReport && fSmartReport.all && (
                    <div className="w-60p bg-darwin-neutrals-50 flex flex-col extracted-values h-full overflow-hidden">
                        <div className="w-full pl-8 border-b border-neutral-8 select-none">
                            <div className="flex items-center font-600 text-14 leading-28 cursor-pointer ml-8">
                                {smartReport && fSmartReport && fSmartReport.all && (
                                    <div
                                        className={
                                            tabType === 'smart-report'
                                                ? 'flex items-center space-x-4 border-b-2 border-actionPrimary text-14 font-700'
                                                : 'flex items-center space-x-4 border-b-2 border-transparent text-14 font-700'
                                        }
                                        onClick={() => this.setTabType('smart-report')}
                                    >
                                        <p
                                            className={
                                                tabType === 'smart-report'
                                                    ? 'flex items-center space-x-4 py-10 px-16 text-actionPrimary text-14 font-700'
                                                    : 'flex items-center space-x-4 py-8 px-16 -darwin-neutral_new-600 text-14 font-700'
                                            }
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="17"
                                                height="16"
                                                viewBox="0 0 17 16"
                                                fill="none"
                                            >
                                                <rect
                                                    x="0.876953"
                                                    width="16"
                                                    height="16"
                                                    rx="8"
                                                    fill={
                                                        tabType === 'smart-report'
                                                            ? '#004BA8'
                                                            : '#767676'
                                                    }
                                                />
                                                <path
                                                    d="M9.32812 3.36328L10.6172 6.13477L13.3887 7.42383C13.5605 7.50977 13.6895 7.68164 13.6895 7.875C13.6895 8.08984 13.5605 8.26172 13.3887 8.34766L10.6172 9.63672L9.32812 12.4082C9.24219 12.5801 9.07031 12.6875 8.87695 12.6875C8.66211 12.6875 8.49023 12.5801 8.4043 12.4082L7.11523 9.63672L4.34375 8.34766C4.17188 8.26172 4.06445 8.08984 4.06445 7.875C4.06445 7.68164 4.17188 7.50977 4.34375 7.42383L7.11523 6.13477L8.4043 3.36328C8.49023 3.19141 8.66211 3.0625 8.87695 3.0625C9.07031 3.0625 9.24219 3.19141 9.32812 3.36328Z"
                                                    fill="white"
                                                />
                                            </svg>
                                            <span>Smart Report</span>
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        {smartReport && fSmartReport && fSmartReport.all && (
                            <>
                                <div className="py-12 px-16 space-y-8 bg-white">
                                    <div className="flex gap-8 flex-nowrap items-center overflow-x-scroll hide-scrollbar">
                                        {filterKeys.map((v) =>
                                            smartVitalFilters[v].length !== 0 ? (
                                                <div
                                                    key={smartVitalFilters[v].filter_id}
                                                    onClick={smartVitalFilters[v].callback}
                                                    className={`horizontal_scroll_element w-auto max-w-full items-center rounded-8 flex gap-4 px-12 py-8 cursor-pointer border ${
                                                        this.state.filter_id ===
                                                        smartVitalFilters[v].filter_id
                                                            ? 'border-darwin-accent-bright-blue-800 bg-darwin-accent-bright-blue-100'
                                                            : 'border-darwin-neutral_new-800 bg-white'
                                                    }`}
                                                >
                                                    {v === 'Unrecognized' && (
                                                        <svg
                                                            width="18"
                                                            height="14"
                                                            viewBox="0 0 18 14"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M9 0C9.4375 0 9.84375 0.25 10.0625 0.625L16.8125 12.125C17.0312 12.5312 17.0312 13 16.8125 13.375C16.5938 13.7812 16.1875 14 15.75 14H2.25C1.78125 14 1.375 13.7812 1.15625 13.375C0.9375 13 0.9375 12.5312 1.15625 12.125L7.90625 0.625C8.125 0.25 8.53125 0 9 0ZM9 4C8.5625 4 8.25 4.34375 8.25 4.75V8.25C8.25 8.6875 8.5625 9 9 9C9.40625 9 9.75 8.6875 9.75 8.25V4.75C9.75 4.34375 9.40625 4 9 4ZM10 11C10 10.4688 9.53125 10 9 10C8.4375 10 8 10.4688 8 11C8 11.5625 8.4375 12 9 12C9.53125 12 10 11.5625 10 11Z"
                                                                fill="#D74B41"
                                                            />
                                                        </svg>
                                                    )}
                                                    <p
                                                        className={`text-13${
                                                            this.state.filter_id ===
                                                            smartVitalFilters[v].filter_id
                                                                ? ' text-darwin-accent-bright-blue-800 font-700'
                                                                : ' font-400'
                                                        }`}
                                                    >{`${v} (${smartVitalFilters[v].length})`}</p>
                                                </div>
                                            ) : null,
                                        )}
                                    </div>

                                    <div
                                        className={`flex items-center space-x-8 bg-white border border-darwin-neutral_new-200 px-8 py-8 rounded-8 ${
                                            selectedSRVitals.size === 0 && 'mt-16'
                                        }`}
                                    >
                                        <div className="flex items-center justify-center w-16 h-16">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                                className="w-16 h-16 fill-darwin-neutral-600"
                                            >
                                                <path d="M504.1 471l-134-134C399.1 301.5 415.1 256.8 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c48.79 0 93.55-16.91 129-45.04l134 134C475.7 509.7 481.9 512 488 512s12.28-2.344 16.97-7.031C514.3 495.6 514.3 480.4 504.1 471zM48 208c0-88.22 71.78-160 160-160s160 71.78 160 160s-71.78 160-160 160S48 296.2 48 208z"></path>
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            className="body-2 bg-transparent w-full"
                                            placeholder="Search vital"
                                            value={vitalSearch}
                                            onChange={this.onVitalSearch}
                                            id="vital-search"
                                        />
                                    </div>
                                </div>

                                <div className="overflow-auto flex-1">
                                    {this.props.copyToPrescription && (
                                        <div className="px-16 flex items-center justify-between">
                                            <div
                                                onClick={() =>
                                                    this.setState({
                                                        selectedSRVitals:
                                                            selectedSRVitals.size === 0
                                                                ? new Set(
                                                                      finalListVerified?.map(
                                                                          (v) => v.id,
                                                                      ),
                                                                  )
                                                                : new Set(),
                                                    })
                                                }
                                                className="flex items-center my-8 space-x-8 cursor-pointer"
                                            >
                                                <div className="flex items-center justify-center w-16 h-16 ">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 448 512"
                                                        className={`${
                                                            selectedSRVitals.size > 0
                                                                ? 'fill-darwin-accent-bright-blue-800'
                                                                : ''
                                                        } w-16 h-16`}
                                                    >
                                                        {selectedSRVitals.size > 0 ? (
                                                            <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM339.8 211.8C350.7 200.9 350.7 183.1 339.8 172.2C328.9 161.3 311.1 161.3 300.2 172.2L192 280.4L147.8 236.2C136.9 225.3 119.1 225.3 108.2 236.2C97.27 247.1 97.27 264.9 108.2 275.8L172.2 339.8C183.1 350.7 200.9 350.7 211.8 339.8L339.8 211.8z"></path>
                                                        ) : (
                                                            <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM384 80H64C55.16 80 48 87.16 48 96V416C48 424.8 55.16 432 64 432H384C392.8 432 400 424.8 400 416V96C400 87.16 392.8 80 384 80z"></path>
                                                        )}
                                                    </svg>
                                                </div>
                                                <span className="caption-1 bold">
                                                    {selectedSRVitals.size === 0
                                                        ? 'Select all values'
                                                        : `${selectedSRVitals.size} selected`}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {fSmartReport.all.map((t) => (
                                        <div
                                            key={t.id}
                                            className={`py-8 flex items-start justify-between border-b border-darwin-neutral-100 value-item px-16 ${
                                                selectedSRVitals.has(t.id)
                                                    ? 'bg-darwin-accent-bright-blue-100'
                                                    : 'bg-white'
                                            }`}
                                        >
                                            <div className="flex items-start space-x-8">
                                                {this.props.copyToPrescription ? (
                                                    t.unverified ? (
                                                        <AntTooltip
                                                            content={`Vital can't be selected because it is unrecognized`}
                                                        >
                                                            <div className="flex items-center justify-center w-16 h-20">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 448 512"
                                                                    className="fill-darwin-neutral_new-200 w-16 h-16"
                                                                >
                                                                    <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM384 80H64C55.16 80 48 87.16 48 96V416C48 424.8 55.16 432 64 432H384C392.8 432 400 424.8 400 416V96C400 87.16 392.8 80 384 80z"></path>
                                                                </svg>
                                                            </div>
                                                        </AntTooltip>
                                                    ) : (
                                                        <div
                                                            onClick={() =>
                                                                t.eka_id &&
                                                                this.onToggleVitalCheckbox(t.id)
                                                            }
                                                            className="flex items-center justify-center w-16 h-20 cursor-pointer"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 448 512"
                                                                className={`${
                                                                    finalVitalList &&
                                                                    selectedSRVitals.has(t.id)
                                                                        ? 'fill-darwin-accent-bright-blue-800'
                                                                        : 'fill-darwin-neutral-600'
                                                                } w-16 h-16`}
                                                            >
                                                                {finalVitalList &&
                                                                selectedSRVitals.has(t.id) ? (
                                                                    <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM339.8 211.8C350.7 200.9 350.7 183.1 339.8 172.2C328.9 161.3 311.1 161.3 300.2 172.2L192 280.4L147.8 236.2C136.9 225.3 119.1 225.3 108.2 236.2C97.27 247.1 97.27 264.9 108.2 275.8L172.2 339.8C183.1 350.7 200.9 350.7 211.8 339.8L339.8 211.8z"></path>
                                                                ) : (
                                                                    <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM384 80H64C55.16 80 48 87.16 48 96V416C48 424.8 55.16 432 64 432H384C392.8 432 400 424.8 400 416V96C400 87.16 392.8 80 384 80z"></path>
                                                                )}
                                                            </svg>
                                                        </div>
                                                    )
                                                ) : null}
                                                <div className="space-y-4">
                                                    <div className="flex gap-8 items-center">
                                                        <p
                                                            onClick={() =>
                                                                this.props.copyToPrescription &&
                                                                !t.unverified &&
                                                                t.eka_id &&
                                                                this.onToggleVitalCheckbox(t.id)
                                                            }
                                                            className={`${
                                                                this.props.copyToPrescription &&
                                                                !t.unverified &&
                                                                t.eka_id &&
                                                                'cursor-pointer'
                                                            } text-darwin-neutral-1000 font-400 flex items-center gap-4 text-13`}
                                                        >
                                                            {t.name}{' '}
                                                            {t.unverified && (
                                                                <AntTooltip
                                                                    placement="right"
                                                                    content={
                                                                        <div className="flex p-12 gap-4">
                                                                            {infoIcon(
                                                                                'flex-shrink-0 fill-white relative top-2 self-start',
                                                                            )}
                                                                            <div className="flex flex-col gap-4">
                                                                                <p className="text-13 font-700">
                                                                                    Unrecognized
                                                                                    vital
                                                                                </p>
                                                                                <p>
                                                                                    This vital is
                                                                                    not recognised
                                                                                    by Eka system.
                                                                                    We can't copy
                                                                                    this vital to Rx
                                                                                    or show any
                                                                                    insights.
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    }
                                                                >
                                                                    <svg
                                                                        width="14"
                                                                        height="13"
                                                                        viewBox="0 0 14 13"
                                                                        fill="none"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path
                                                                            d="M7 0.9375C7.35547 0.9375 7.68555 1.14062 7.86328 1.44531L13.3477 10.7891C13.5254 11.1191 13.5254 11.5 13.3477 11.8047C13.1699 12.1348 12.8398 12.3125 12.4844 12.3125H1.51562C1.13477 12.3125 0.804688 12.1348 0.626953 11.8047C0.449219 11.5 0.449219 11.1191 0.626953 10.7891L6.11133 1.44531C6.28906 1.14062 6.61914 0.9375 7 0.9375ZM7 4.1875C6.64453 4.1875 6.39062 4.4668 6.39062 4.79688V7.64062C6.39062 7.99609 6.64453 8.25 7 8.25C7.33008 8.25 7.60938 7.99609 7.60938 7.64062V4.79688C7.60938 4.4668 7.33008 4.1875 7 4.1875ZM7.8125 9.875C7.8125 9.44336 7.43164 9.0625 7 9.0625C6.54297 9.0625 6.1875 9.44336 6.1875 9.875C6.1875 10.332 6.54297 10.6875 7 10.6875C7.43164 10.6875 7.8125 10.332 7.8125 9.875Z"
                                                                            fill="#D74B41"
                                                                        />
                                                                    </svg>
                                                                </AntTooltip>
                                                            )}
                                                        </p>
                                                        {t.unverified ? null : this.state
                                                              .keyVitals &&
                                                          this.state.keyVitals.length !== 0 &&
                                                          t.eka_id &&
                                                          this.state.keyVitals.includes(
                                                              t.eka_id,
                                                          ) ? (
                                                            <AntTooltip
                                                                placement="right"
                                                                content="Key vital"
                                                            >
                                                                <svg
                                                                    width="15"
                                                                    height="14"
                                                                    viewBox="0 0 14 14"
                                                                    fill="none"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    onClick={() =>
                                                                        this.toggleKeyVital(
                                                                            t.eka_id as string,
                                                                        )
                                                                    }
                                                                    className="cursor-pointer text-13"
                                                                >
                                                                    <path
                                                                        d="M7.71094 1.08203L9.36133 4.45898L12.9922 4.99219C13.2969 5.04297 13.5508 5.24609 13.6523 5.55078C13.7539 5.83008 13.6777 6.16016 13.4492 6.36328L10.8086 8.97852L11.4434 12.6855C11.4941 12.9902 11.3672 13.2949 11.1133 13.4727C10.8594 13.6758 10.5293 13.6758 10.25 13.5488L7 11.7969L3.72461 13.5488C3.4707 13.6758 3.14062 13.6758 2.88672 13.4727C2.63281 13.2949 2.50586 12.9902 2.55664 12.6855L3.16602 8.97852L0.525391 6.36328C0.322266 6.16016 0.246094 5.83008 0.322266 5.55078C0.423828 5.24609 0.677734 5.04297 0.982422 4.99219L4.63867 4.45898L6.26367 1.08203C6.39062 0.802734 6.66992 0.625 7 0.625C7.30469 0.625 7.58398 0.802734 7.71094 1.08203Z"
                                                                        fill="#EDAE0C"
                                                                    />
                                                                </svg>
                                                            </AntTooltip>
                                                        ) : (
                                                            <AntTooltip
                                                                placement="right"
                                                                content="Add as key vital"
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 576 512"
                                                                    width="15"
                                                                    height="14"
                                                                    onClick={() =>
                                                                        this.toggleKeyVital(
                                                                            t.eka_id as string,
                                                                        )
                                                                    }
                                                                    className="cursor-pointer text-13"
                                                                >
                                                                    <path d="M287.9 0c9.2 0 17.6 5.2 21.6 13.5l68.6 141.3 153.2 22.6c9 1.3 16.5 7.6 19.3 16.3s.5 18.1-5.9 24.5L433.6 328.4l26.2 155.6c1.5 9-2.2 18.1-9.6 23.5s-17.3 6-25.3 1.7l-137-73.2L151 509.1c-8.1 4.3-17.9 3.7-25.3-1.7s-11.2-14.5-9.7-23.5l26.2-155.6L31.1 218.2c-6.5-6.4-8.7-15.9-5.9-24.5s10.3-14.9 19.3-16.3l153.2-22.6L266.3 13.5C270.4 5.2 278.7 0 287.9 0zm0 79L235.4 187.2c-3.5 7.1-10.2 12.1-18.1 13.3L99 217.9 184.9 303c5.5 5.5 8.1 13.3 6.8 21L171.4 443.7l105.2-56.2c7.1-3.8 15.6-3.8 22.6 0l105.2 56.2L384.2 324.1c-1.3-7.7 1.2-15.5 6.8-21l85.9-85.1L358.6 200.5c-7.8-1.2-14.6-6.1-18.1-13.3L287.9 79z" />
                                                                </svg>
                                                            </AntTooltip>
                                                        )}
                                                    </div>
                                                    <p className="text-darwin-neutral-600 font-400 text-11">
                                                        {t.range}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end space-y-4">
                                                <p className="text-darwin-neutral_new-1000 font-400 text-13">
                                                    {t.value}
                                                </p>
                                                <div className="flex space-x-8 items-center">
                                                    {t.result_id === NO_INTERPRETATION_VITAL && (
                                                        <AntTooltip
                                                            placement="left"
                                                            content="Interpretation could not be determined"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 512 512"
                                                                className="w-16 h-16"
                                                                fill="#404040"
                                                            >
                                                                <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 400c-18 0-32-14-32-32s13.1-32 32-32c17.1 0 32 14 32 32S273.1 400 256 400zM325.1 258L280 286V288c0 13-11 24-24 24S232 301 232 288V272c0-8 4-16 12-21l57-34C308 213 312 206 312 198C312 186 301.1 176 289.1 176h-51.1C225.1 176 216 186 216 198c0 13-11 24-24 24s-24-11-24-24C168 159 199 128 237.1 128h51.1C329 128 360 159 360 198C360 222 347 245 325.1 258z"></path>
                                                            </svg>
                                                        </AntTooltip>
                                                    )}
                                                    <p
                                                        className={`${getColorClass(
                                                            t.result_id,
                                                        )} flex gap-4 items-center whitespace-nowrap text-11 font-400`}
                                                    >
                                                        {t.result_id &&
                                                        LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION[
                                                            t.result_id
                                                        ] &&
                                                        LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION[
                                                            t.result_id
                                                        ]?.icon
                                                            ? LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION[
                                                                  t.result_id
                                                              ].icon
                                                            : null}
                                                        {t.result_id === NO_INTERPRETATION_VITAL
                                                            ? 'No interpretation'
                                                            : t.display_result || '-'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {this.props.smartReport && this.props.copyToPrescription && (
                                    <div className="px-16 border-t border-darwin-neutral_new-200 justify-between flex gap-16 py-8">
                                        {totalReports && totalReports.length !== 0 ? (
                                            filter_id === 'unrecognized' &&
                                            this.state.selectedSRVitals.size === 0 ? (
                                                <AntTooltip
                                                    placement="top"
                                                    content="You have recognized vitals in this report"
                                                >
                                                    <div
                                                        className="text-13 border cursor-pointer flex-shrink-0 text-center w-192 font-700 rounded-8 px-12 py-10 border border-darwin-neutral_new-200 bg-white"
                                                        style={{ color: '#A3A3A3' }}
                                                    >
                                                        Copy all to Rx
                                                        {this.state.selectedSRVitals.size !== 0 &&
                                                            ` (${this.state.selectedSRVitals.size})`}
                                                    </div>
                                                </AntTooltip>
                                            ) : (
                                                <div
                                                    onClick={() =>
                                                        this.copyAll(
                                                            selectedSRVitals.size > 0
                                                                ? totalReports?.filter((v) =>
                                                                      selectedSRVitals.has(v.id),
                                                                  )
                                                                : finalListVerified,
                                                        )
                                                    }
                                                    className={`text-13 ${
                                                        this.state.selectedSRVitals.size !== 0
                                                            ? 'flex-grow bg-darwin-accent-bright-blue-800 text-white'
                                                            : 'bg-white text-darwin-accent-bright-blue-800'
                                                    } cursor-pointer flex-shrink-0 text-center w-192 font-700 rounded-8 px-12 py-10 border border-darwin-accent-bright-blue-800`}
                                                >
                                                    Copy{' '}
                                                    {this.state.selectedSRVitals.size !== 0
                                                        ? 'selected'
                                                        : 'all'}{' '}
                                                    to Rx
                                                    {this.state.selectedSRVitals.size !== 0
                                                        ? ` (${this.state.selectedSRVitals.size})`
                                                        : ` (${
                                                              finalListVerified
                                                                  ? finalListVerified.length
                                                                  : 0
                                                          })`}
                                                </div>
                                            )
                                        ) : null}
                                        {this.state.selectedSRVitals.size === 0 ? (
                                            this.props.smartReport.verified &&
                                            keyVitalsForCurrentReport &&
                                            keyVitalsForCurrentReport.length !== 0 &&
                                            filter_id !== 'unrecognized' ? (
                                                <div
                                                    onClick={() => {
                                                        this.setState(
                                                            {
                                                                selectedSRVitals: new Set(
                                                                    keyVitalsForCurrentReport!.map(
                                                                        (kv) =>
                                                                            this.props.smartReport!.verified!.find(
                                                                                (v) =>
                                                                                    v.eka_id === kv,
                                                                            )!.id,
                                                                    ),
                                                                ),
                                                            },
                                                            this.copyToPrescription,
                                                        );
                                                    }}
                                                    className="text-13 cursor-pointer text-center w-192 font-700 rounded-8 px-12 py-10 bg-darwin-accent-bright-blue-800 text-white"
                                                >
                                                    Copy key vitals to Rx (
                                                    {keyVitalsForCurrentReport.length})
                                                </div>
                                            ) : (
                                                <AntTooltip
                                                    placement="top"
                                                    content="You have no selected key vitals in this report"
                                                >
                                                    <div
                                                        className="text-13 border cursor-pointer text-center w-192 font-700 rounded-8 px-12 py-10 bg-darwin_neutral_new-400 text-white"
                                                        style={{ backgroundColor: '#BABABA' }}
                                                    >
                                                        Copy key vitals to Rx
                                                    </div>
                                                </AntTooltip>
                                            )
                                        ) : null}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default connect()(MediaViewer);
